/*
 * WOOOD - eigen wishlist
 * localStorage-backed, zonder app-afhankelijkheid. Alle events zijn gedelegeerd
 * op document, zodat AJAX-gerenderde grids (filters, Depict, sliders, quick view)
 * en theme-editor reloads blijven werken.
 *
 * - Eén lijst per browser, los van in- of uitloggen. Een lijst per klant-id
 *   leek netjes, maar in de praktijk was de lijst na uitloggen "weg".
 * - Lijsten zijn deelbaar via <wishlist-pagina>?shared=<handles> en renderen
 *   read-only voor bezoekers.
 * - Kaarten op de wishlist-pagina worden server-side gerenderd via
 *   /products/<handle>?view=wishlist-card, zodat prijzen, valuta, badges en
 *   vertalingen altijd bij de actieve markt horen (zelfde patroon als
 *   'Recently viewed').
 */
(function () {
  'use strict';

  /* Twee keer ingeladen (bijv. een sectie die het script opnieuw invoegt) zou
     elke klik dubbel afhandelen: toevoegen en meteen weer verwijderen. */
  if (window.WOOODWishlist) return;

  var BASE_KEY = 'woood_wishlist_v1';
  var STORAGE_KEY = BASE_KEY;
  var root = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/$/, '');
  var config = window.wishlistConfig || {};
  var strings = window.wishlistStrings || {};
  var customer = config.customer || null;
  var wishlistUrl = config.url || '/pages/wishlist';
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var toastTimer = null;
  var cardCache = {};
  var renderToken = 0;
  var lastToggleAt = {};

  /* ------------------------------------------------------------------ *
   * Opslag
   *
   * Safari en Firefox met geblokkeerde cookies gooien al een SecurityError bij
   * het aanspreken van window.localStorage. Dan houden we de lijst in het
   * geheugen bij, zodat het hartje in elk geval binnen de pagina reageert.
   * `memory` bevat alleen waarden die niet in localStorage terechtkwamen, dus
   * zodra opslag wel werkt blijft localStorage de enige bron (en kloppen andere
   * tabbladen en de terug-knop gewoon).
   * ------------------------------------------------------------------ */

  var memory = {};
  var storageOk = (function () {
    try {
      var probe = '__woood_wishlist_probe';
      window.localStorage.setItem(probe, '1');
      window.localStorage.removeItem(probe);
      return true;
    } catch (e) {
      return false;
    }
  })();

  function storageGet(key) {
    if (Object.prototype.hasOwnProperty.call(memory, key)) return memory[key];
    if (!storageOk) return null;
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function storageSet(key, value) {
    var persisted = false;
    if (storageOk) {
      try {
        window.localStorage.setItem(key, value);
        persisted = true;
      } catch (e) {
        /* quota vol - valt terug op geheugen */
      }
    }
    if (persisted) {
      delete memory[key];
    } else {
      memory[key] = value;
    }
  }

  function storageRemove(key) {
    delete memory[key];
    if (!storageOk) return;
    try {
      window.localStorage.removeItem(key);
    } catch (e) {
      /* noop */
    }
  }

  function storageKeys() {
    var keys = Object.keys(memory);
    if (!storageOk) return keys;
    try {
      return keys.concat(Object.keys(window.localStorage));
    } catch (e) {
      return keys;
    }
  }

  /* ------------------------------------------------------------------ *
   * Store
   * ------------------------------------------------------------------ */

  function readKey(key) {
    try {
      var raw = JSON.parse(storageGet(key));
      return Array.isArray(raw) ? raw.filter(function (item) { return item && item.handle; }) : [];
    } catch (e) {
      return [];
    }
  }

  function read() {
    return readKey(STORAGE_KEY);
  }

  function write(items) {
    storageSet(STORAGE_KEY, JSON.stringify(items));
    broadcast();
  }

  /* Eerdere versie bewaarde ingelogde klanten onder <key>_c<klant-id>. Die
     lijsten eenmalig samenvoegen in de ene browserlijst, zodat niemand items
     kwijtraakt. */
  function migrateCustomerLists() {
    var legacyKeys = storageKeys().filter(function (key) {
      return key.indexOf(BASE_KEY + '_c') === 0;
    });
    if (!legacyKeys.length) return;
    var items = read();
    var handles = items.map(function (item) { return item.handle; });
    legacyKeys.forEach(function (key) {
      readKey(key).forEach(function (item) {
        if (handles.indexOf(item.handle) !== -1) return;
        items.push(item);
        handles.push(item.handle);
      });
    });
    storageSet(STORAGE_KEY, JSON.stringify(items));
    legacyKeys.forEach(storageRemove);
  }

  function has(handle) {
    return read().some(function (item) { return item.handle === handle; });
  }

  function add(handle, id) {
    var items = read();
    if (items.some(function (item) { return item.handle === handle; })) return;
    items.unshift({ handle: handle, id: id ? String(id) : null });
    write(items);
  }

  function remove(handle) {
    write(read().filter(function (item) { return item.handle !== handle; }));
  }

  function toggle(handle, id) {
    if (has(handle)) {
      remove(handle);
      return false;
    }
    add(handle, id);
    return true;
  }

  function clearAll() {
    write([]);
  }

  function broadcast() {
    document.dispatchEvent(new CustomEvent('theme:wishlist:change', { detail: { items: read() } }));
  }

  /* ------------------------------------------------------------------ *
   * Gedeelde lijst
   * ------------------------------------------------------------------ */

  function sharedHandles() {
    var params = new URLSearchParams(window.location.search);
    var shared = params.get('shared');
    if (!shared) return null;
    var handles = shared.split(',').filter(function (handle) {
      return /^[a-z0-9-]+$/.test(handle);
    });
    return handles.length ? handles.slice(0, 100) : null;
  }

  function sharedBy() {
    var params = new URLSearchParams(window.location.search);
    var by = params.get('by');
    return by ? by.slice(0, 40) : null;
  }

  function buildShareUrl() {
    var handles = read().map(function (item) { return item.handle; });
    var url = new URL(wishlistUrl, window.location.origin);
    url.searchParams.set('shared', handles.join(','));
    if (customer && customer.firstName) url.searchParams.set('by', customer.firstName);
    return url.toString();
  }

  /* ------------------------------------------------------------------ *
   * UI sync - knoppen + tellers
   * ------------------------------------------------------------------ */

  function syncButtons(scope) {
    var buttons = (scope || document).querySelectorAll('[data-wishlist-toggle]');
    if (!buttons.length) return;
    /* lijst één keer lezen in plaats van per hartje (homepage heeft er 40+) */
    var saved = {};
    read().forEach(function (item) { saved[item.handle] = true; });
    for (var i = 0; i < buttons.length; i++) {
      var button = buttons[i];
      var active = saved[button.getAttribute('data-wishlist-handle')] === true;
      button.classList.toggle('is-active', active);
      /* wk-selected houdt de bestaande WOOOD styling van het gevulde hart intact */
      button.classList.toggle('wk-selected', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      var label = active ? button.getAttribute('data-label-remove') : button.getAttribute('data-label-add');
      if (label) button.setAttribute('aria-label', label);
    }
  }

  function syncCounters() {
    var count = read().length;
    var counters = document.querySelectorAll('[data-wishlist-count]');
    for (var i = 0; i < counters.length; i++) {
      var counter = counters[i];
      var previous = counter.textContent;
      counter.textContent = count > 99 ? '99+' : String(count);
      counter.classList.toggle('is-visible', count > 0);
      if (count > 0 && previous !== counter.textContent) {
        counter.classList.remove('is-bumped');
        void counter.offsetWidth; /* animatie opnieuw starten */
        counter.classList.add('is-bumped');
      }
    }
  }

  function syncAll(scope) {
    syncButtons(scope);
    syncCounters();
  }

  /* ------------------------------------------------------------------ *
   * Micro-interacties
   * ------------------------------------------------------------------ */

  function burst(button) {
    if (reducedMotion) return;
    var old = button.querySelector('.wishlist-button__burst');
    if (old) old.remove();
    var holder = document.createElement('span');
    holder.className = 'wishlist-button__burst';
    holder.setAttribute('aria-hidden', 'true');
    for (var i = 0; i < 8; i++) {
      var particle = document.createElement('span');
      particle.className = 'wishlist-button__particle';
      particle.style.setProperty('--angle', (i * 45) + 'deg');
      holder.appendChild(particle);
    }
    button.appendChild(holder);
    window.setTimeout(function () { holder.remove(); }, 700);
  }

  function toast(message, showLink) {
    var element = document.querySelector('[data-wishlist-toast]');
    if (!element) {
      element = document.createElement('div');
      element.setAttribute('data-wishlist-toast', '');
      element.className = 'wishlist-toast';
      element.setAttribute('role', 'status');
      element.setAttribute('aria-live', 'polite');
      element.innerHTML =
        '<span class="wishlist-toast__icon"></span>' +
        '<span class="wishlist-toast__text"></span>' +
        '<a class="wishlist-toast__link" href="' + escapeAttribute(wishlistUrl) + '"></a>';
      var iconHolder = element.querySelector('.wishlist-toast__icon');
      var iconSource = document.querySelector('.wishlist-button .icon-wishlist-heart');
      if (iconSource) iconHolder.appendChild(iconSource.cloneNode(true));
      document.body.appendChild(element);
    }
    element.querySelector('.wishlist-toast__text').textContent = message;
    var link = element.querySelector('.wishlist-toast__link');
    link.textContent = strings.view || 'Bekijk';
    link.style.display = showLink ? '' : 'none';
    element.classList.add('is-visible');
    if (toastTimer) window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(function () {
      element.classList.remove('is-visible');
    }, 2600);
  }

  function escapeAttribute(value) {
    return String(value == null ? '' : value).replace(/"/g, '&quot;');
  }

  /* ------------------------------------------------------------------ *
   * Wishlist-pagina
   * ------------------------------------------------------------------ */

  function wait(ms) {
    return new Promise(function (resolve) { window.setTimeout(resolve, ms); });
  }

  function fetchWithRetry(url, attempt) {
    return window.fetch(url, { credentials: 'same-origin' }).then(function (response) {
      /* Shopify geeft 429 bij veel gelijktijdige verzoeken: even wachten en opnieuw */
      if (response.status === 429 && attempt < 3) {
        return wait(600 * (attempt + 1)).then(function () { return fetchWithRetry(url, attempt + 1); });
      }
      return response;
    });
  }

  /* De kaart wordt server-side gerenderd, zodat prijs, valuta, badges,
     kleurstalen en vertalingen kloppen met de actieve markt.

     Een 404 betekent hier níet dat het product weg is: woood.nl draait meerdere
     markten op hetzelfde domein (/nl-be, /fr-be, /en-eu, ...) die samen één
     opgeslagen lijst delen. Een product dat alleen in NL verkocht wordt geeft in
     /nl-be een 404. Vroeger werd het dan uit de lijst gewist, ook voor NL. Nu
     wordt het in die markt alleen niet getoond en blijft het bewaard. */
  function fetchCard(handle) {
    var key = root + '|' + handle;
    if (cardCache[key]) return Promise.resolve(cardCache[key]);
    return fetchWithRetry(root + '/products/' + encodeURIComponent(handle) + '?view=wishlist-card', 0)
      .then(function (response) {
        if (!response.ok) return null;
        return response.text();
      })
      .then(function (html) {
        if (!html) return null;
        var trimmed = html.trim();
        /* Leeg = uitgesloten product (bijv. plaatsingservice). Een volledige
           pagina (theme zonder deze template, foutpagina van een proxy) mag nooit
           als kaart in het grid belanden. */
        if (!trimmed.length || /^<(?:!doctype|html)\b/i.test(trimmed)) return null;
        cardCache[key] = trimmed;
        return trimmed;
      })
      .catch(function () { return null; });
  }

  /* Maximaal `limit` verzoeken tegelijk, resultaten in de oorspronkelijke volgorde */
  function mapLimit(list, limit, fn) {
    return new Promise(function (resolve) {
      var results = new Array(list.length);
      var next = 0;
      var active = 0;
      var finished = 0;
      if (!list.length) {
        resolve(results);
        return;
      }
      function launch() {
        while (active < limit && next < list.length) {
          (function (index) {
            active++;
            Promise.resolve()
              .then(function () { return fn(list[index], index); })
              .then(function (value) { results[index] = value; }, function () { results[index] = null; })
              .then(function () {
                active--;
                finished++;
                if (finished === list.length) resolve(results);
                else launch();
              });
          })(next++);
        }
      }
      launch();
    });
  }

  function pageElements() {
    var wrapper = document.querySelector('[data-wishlist-page]');
    if (!wrapper) return null;
    return {
      wrapper: wrapper,
      grid: wrapper.querySelector('[data-wishlist-grid]'),
      empty: wrapper.querySelector('[data-wishlist-empty]'),
      count: wrapper.querySelector('[data-wishlist-page-count]'),
      toolbar: wrapper.querySelector('[data-wishlist-toolbar]'),
      sharedBanner: wrapper.querySelector('[data-wishlist-shared-banner]'),
      sharedByLabel: wrapper.querySelector('[data-wishlist-shared-by]')
    };
  }

  function updateCountLabel(elements, count) {
    if (!elements.count) return;
    var template = elements.count.getAttribute(count === 1 ? 'data-text-one' : 'data-text-other') || '';
    elements.count.textContent = template.replace('[[count]]', count);
    elements.count.classList.toggle('is-hidden', count === 0);
  }

  function renderSkeletons(grid, count) {
    grid.innerHTML = '';
    for (var i = 0; i < count; i++) {
      var skeleton = document.createElement('div');
      skeleton.className = 'wishlist-grid__item wishlist-grid__item--skeleton';
      skeleton.innerHTML =
        '<div class="wishlist-skeleton-media"></div>' +
        '<span class="wishlist-skeleton-line"></span>' +
        '<span class="wishlist-skeleton-line wishlist-skeleton-line--short"></span>';
      grid.appendChild(skeleton);
    }
  }

  function renderList(elements, handles, options) {
    if (!handles.length) {
      renderToken++;
      elements.grid.innerHTML = '';
      elements.grid.classList.add('is-hidden');
      if (elements.empty) elements.empty.classList.remove('is-hidden');
      if (elements.toolbar) elements.toolbar.classList.add('is-hidden');
      updateCountLabel(elements, 0);
      return;
    }

    elements.grid.classList.remove('is-hidden');
    if (elements.empty) elements.empty.classList.add('is-hidden');
    if (elements.toolbar) elements.toolbar.classList.toggle('is-hidden', !!options.shared);
    renderSkeletons(elements.grid, handles.length);

    /* Een nieuwere render (ander tabblad, theme editor, lijst gewist) mag niet
       overschreven worden door een tragere oudere. */
    var token = ++renderToken;
    mapLimit(handles, 6, function (handle) { return fetchCard(handle); }).then(function (cards) {
      if (token !== renderToken) return;
      elements.grid.innerHTML = '';
      var rendered = 0;
      cards.forEach(function (html, index) {
        if (!html) return;
        var item = document.createElement('div');
        item.className = 'wishlist-grid__item';
        item.setAttribute('data-wishlist-card', '');
        item.setAttribute('data-handle', handles[index]);
        if (!reducedMotion) item.style.setProperty('--reveal-delay', Math.min(index * 55, 440) + 'ms');
        item.innerHTML = html;
        elements.grid.appendChild(item);
        rendered++;
      });
      updateCountLabel(elements, rendered);
      if (!rendered) {
        elements.grid.classList.add('is-hidden');
        if (elements.toolbar) elements.toolbar.classList.add('is-hidden');
        if (elements.empty) elements.empty.classList.remove('is-hidden');
      }
      syncButtons(elements.grid);
    });
  }

  function renderPage() {
    var elements = pageElements();
    if (!elements || !elements.grid) return;

    var shared = sharedHandles();
    if (shared) {
      elements.wrapper.classList.add('wishlist-page--shared');
      if (elements.sharedBanner) {
        elements.sharedBanner.classList.remove('is-hidden');
        var by = sharedBy();
        if (by && elements.sharedByLabel) {
          var template = elements.sharedByLabel.getAttribute('data-text-by') || '';
          elements.sharedByLabel.textContent = template.replace('[[name]]', by);
        }
      }
      renderList(elements, shared, { shared: true });
      return;
    }

    renderList(elements, read().map(function (item) { return item.handle; }), { shared: false });
  }

  function removeCard(handle) {
    var card = document.querySelector('[data-wishlist-card][data-handle="' + handle + '"]');
    if (!card) return;
    var done = false;
    var finish = function () {
      if (done) return;
      done = true;
      card.remove();
      afterRemoval();
    };
    if (reducedMotion) {
      finish();
      return;
    }
    card.classList.add('is-leaving');
    card.addEventListener('transitionend', finish, { once: true });
    window.setTimeout(finish, 450); /* vangnet als transitionend nooit afgaat */
  }

  function afterRemoval() {
    var elements = pageElements();
    if (!elements || !elements.grid || elements.wrapper.classList.contains('wishlist-page--shared')) return;
    var left = elements.grid.querySelectorAll('[data-wishlist-card]').length;
    updateCountLabel(elements, left);
    if (!left) {
      elements.grid.classList.add('is-hidden');
      if (elements.toolbar) elements.toolbar.classList.add('is-hidden');
      if (elements.empty) elements.empty.classList.remove('is-hidden');
    }
  }

  /* ------------------------------------------------------------------ *
   * Delen / alles bewaren / wissen
   * ------------------------------------------------------------------ */

  function share(button) {
    var url = buildShareUrl();
    var text = button.getAttribute('data-share-text') || '';
    if (window.navigator.share) {
      window.navigator.share({ title: document.title, text: text, url: url }).catch(function () {
        /* bezoeker heeft het deelvenster gesloten - niets te doen */
      });
      return;
    }
    var copied = button.getAttribute('data-text-copied') || 'Link gekopieerd';
    var done = function () {
      button.classList.add('is-done');
      window.setTimeout(function () { button.classList.remove('is-done'); }, 2000);
      toast(copied, false);
    };
    if (window.navigator.clipboard) {
      window.navigator.clipboard.writeText(url).then(done).catch(function () {
        window.prompt(copied, url);
      });
    } else {
      window.prompt(copied, url);
    }
  }

  function saveShared() {
    var handles = sharedHandles() || [];
    handles
      .slice()
      .reverse()
      .forEach(function (handle) {
        if (!has(handle)) add(handle, null);
      });
    toast(strings.added || 'Toegevoegd aan je verlanglijst', true);
  }

  /* ------------------------------------------------------------------ *
   * Events - alles gedelegeerd op document
   * ------------------------------------------------------------------ */

  /* In de capture-fase: dan zijn wij als eerste aan de beurt en kan een script op
     een wrapper (kaartlink via JS, slider, app-widget) de klik niet eerst
     afvangen of er een navigatie van maken. */
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (target && target.nodeType !== 1) target = target.parentElement;
    if (!target || !target.closest) return;

    var toggleButton = target.closest('[data-wishlist-toggle]');
    if (toggleButton) {
      event.preventDefault();
      event.stopPropagation();
      var handle = toggleButton.getAttribute('data-wishlist-handle');
      if (!handle) return;
      /* Een dubbelklik (of dubbeltik) is twee kliks: zonder deze drempel voegt de
         eerste toe en haalt de tweede het meteen weer weg. */
      var now = Date.now();
      if (lastToggleAt[handle] && now - lastToggleAt[handle] < 450) return;
      lastToggleAt[handle] = now;
      var added = toggle(handle, toggleButton.getAttribute('data-wishlist-id'));
      var onSharedView = !!toggleButton.closest('.wishlist-page--shared');
      if (added) {
        burst(toggleButton);
        toast(strings.added || 'Toegevoegd aan je verlanglijst', true);
      } else {
        toast(strings.removed || 'Verwijderd uit je verlanglijst', false);
        if (!onSharedView && document.querySelector('[data-wishlist-grid]')) removeCard(handle);
      }
      return;
    }

    var removeButton = target.closest('[data-wishlist-remove]');
    if (removeButton) {
      event.preventDefault();
      var removeHandle = removeButton.getAttribute('data-wishlist-handle');
      remove(removeHandle);
      toast(strings.removed || 'Verwijderd uit je verlanglijst', false);
      removeCard(removeHandle);
      return;
    }

    var shareButton = target.closest('[data-wishlist-share]');
    if (shareButton) {
      event.preventDefault();
      share(shareButton);
      return;
    }

    var saveAllButton = target.closest('[data-wishlist-save-all]');
    if (saveAllButton) {
      event.preventDefault();
      saveShared();
      return;
    }

    var clearButton = target.closest('[data-wishlist-clear]');
    if (clearButton) {
      event.preventDefault();
      /* twee stappen: eerste klik wapent de knop, tweede klik wist de lijst */
      if (!clearButton.classList.contains('is-armed')) {
        clearButton.classList.add('is-armed');
        window.setTimeout(function () { clearButton.classList.remove('is-armed'); }, 3500);
        return;
      }
      clearButton.classList.remove('is-armed');
      clearAll();
      renderPage();
      return;
    }
  }, true);

  /* opnieuw synchroniseren bij wijzigingen in dit tabblad */
  document.addEventListener('theme:wishlist:change', function () {
    syncAll();
  });

  /* synchronisatie tussen tabbladen (key null = localStorage.clear()) */
  window.addEventListener('storage', function (event) {
    if (event.key !== null && event.key.indexOf(BASE_KEY) !== 0) return;
    syncAll();
    renderPage();
  });

  /* Terug-knop: de browser zet de vorige pagina uit zijn cache terug zonder
     scripts opnieuw te draaien. Een hartje dat intussen op een andere pagina is
     gezet, stond dan nog leeg. */
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    syncAll();
    renderPage();
  });

  /* AJAX-gerenderde grids (filteren, sorteren, volgende pagina, Depict, sliders,
     quick view). Filteren vervangt tientallen kaarten in één keer; alleen het
     eerste nieuwe blok synchroniseren liet de rest leeg lijken, en een klik op zo'n
     "leeg" hartje verwijderde het product juist. Daarom per frame hooguit één
     volledige sync. */
  var syncQueued = false;
  function queueSync() {
    if (syncQueued) return;
    syncQueued = true;
    (window.requestAnimationFrame || window.setTimeout)(function () {
      syncQueued = false;
      syncButtons();
    });
  }

  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) continue;
        if ((node.matches && node.matches('[data-wishlist-toggle]')) || (node.querySelector && node.querySelector('[data-wishlist-toggle]'))) {
          queueSync();
          return;
        }
      }
    }
  });

  function init() {
    migrateCustomerLists();
    syncAll();
    renderPage();
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* theme editor rendert secties opnieuw */
  document.addEventListener('shopify:section:load', function () {
    syncAll();
    renderPage();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.WOOODWishlist = {
    items: read,
    has: has,
    add: add,
    remove: remove,
    toggle: toggle,
    clear: clearAll,
    shareUrl: buildShareUrl,
    count: function () { return read().length; }
  };
})();
